import { ref, onMounted } from 'vue'

/**
 * Composable for fetching a single resource.
 *
 * @param {Function} fetchFn - Async function that returns the API response
 * @param {Object} options
 * @param {boolean} options.immediate - Auto-fetch on mount (default: false)
 * @param {Function} options.extract - Custom extractor (default: res => res.data)
 * @param {*} options.defaultValue - Initial value for data before first fetch (default: null)
 *
 * @example
 * const { data, loading, fetch } = useFetch(() => getAdminById(id))
 * await fetch()
 *
 * @example with immediate
 * const { data, loading } = useFetch(() => fetchProfile(), { immediate: true })
 */
export function useFetch(fetchFn, options = {}) {
  const { immediate = false, extract = res => res.data, defaultValue = null } = options

  const data = ref(defaultValue)
  const loading = ref(false)

  async function fetch() {
    try {
      loading.value = true
      const res = await fetchFn()
      data.value = extract(res)
    } catch (e) {
      // error handled by request interceptor
    } finally {
      loading.value = false
    }
  }

  if (immediate) {
    onMounted(fetch)
  }

  return { data, loading, fetch }
}
